<script setup>
import { computed, ref } from 'vue';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { X, Palette, Eye, Upload } from 'lucide-vue-next';
import ColorThief from '@/lib/color-reader';
import { $t } from '@/i18n';

const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:open', 'theme-created']);

const showDialog = computed({
  get: () => props.open,
  set: (v) => emit('update:open', v),
});

// 自定义主题表单数据
const customThemeForm = ref({
  label: '',
  description: '',
  colors: ['#1a1a1a', '#ffffff', '#f5f5f5', '#e5e5e5'],
  primaryColor: '#1a1a1a',
  secondaryColor: '#ffffff',
});

// 图片上传相关
const imagePreview = ref(null);
const isExtracting = ref(false);
const fileInput = ref(null);

// 重置表单
function resetForm() {
  customThemeForm.value = {
    label: '',
    description: '',
    colors: ['#1a1a1a', '#ffffff', '#f5f5f5', '#e5e5e5'],
    primaryColor: '#1a1a1a',
    secondaryColor: '#ffffff',
  };
  imagePreview.value = null;
}

// 保存自定义主题
function saveCustomTheme() {
  if (!customThemeForm.value.label.trim()) {
    return;
  }

  const newTheme = {
    ...customThemeForm.value,
    value: `custom-${Date.now()}`,
    isCustom: true,
  };

  emit('theme-created', newTheme);
  showDialog.value = false;
  resetForm();
}

// 更新自定义主题表单中的颜色
function updateCustomColor(index, color) {
  customThemeForm.value.colors[index] = color;
  if (index === 0) {
    customThemeForm.value.primaryColor = color;
  } else if (index === 1) {
    customThemeForm.value.secondaryColor = color;
  }
}

// 监听对话框关闭，重置表单
function onDialogClose() {
  showDialog.value = false;
  resetForm();
}

// 触发图片上传
function triggerImageUpload() {
  fileInput.value?.click();
}

// 处理图片上传
function handleImageUpload(event) {
  const file = event.target.files[0];
  if (!file) return;

  // 验证文件类型和大小
  if (!file.type.startsWith('image/')) {
    alert($t('themeSelector.pleaseSelectImage'));
    return;
  }

  if (file.size > 5 * 1024 * 1024) {
    alert($t('themeSelector.imageSizeLimit'));
    return;
  }

  // 创建预览
  const reader = new FileReader();
  reader.onload = (e) => {
    imagePreview.value = e.target.result;
    extractColorsFromImage(e.target.result);
  };
  reader.readAsDataURL(file);
}

// 移除图片
function removeImage() {
  imagePreview.value = null;
  if (fileInput.value) {
    fileInput.value.value = '';
  }
}

// 使用 ColorThief 提取颜色
function extractColorsFromImage(imageSrc) {
  isExtracting.value = true;

  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      try {
        var colorThief = new ColorThief();
        const palette = colorThief.getPalette(img, 8);

        if (palette && palette.length > 0) {
          // 将 RGB 值转换为十六进制
          const extractedColors = palette.slice(0, 4).map((rgb) => {
            const [r, g, b] = rgb;
            return `#${r.toString(16).padStart(2, '0')}${g
              .toString(16)
              .padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          });

          // 确保有足够的颜色，如果不够则用默认颜色补充
          while (extractedColors.length < 4) {
            extractedColors.push(customThemeForm.value.colors[extractedColors.length]);
          }

          // 更新表单颜色
          customThemeForm.value.colors = extractedColors;
          customThemeForm.value.primaryColor = extractedColors[0];
          customThemeForm.value.secondaryColor = extractedColors[1];

          // 如果主题名称为空，自动生成一个
          if (!customThemeForm.value.label.trim()) {
            customThemeForm.value.label = `Colors`;
          }
        }
      } catch (error) {
        console.error(error);
        alert($t('themeSelector.colorExtractionFailed'));
      } finally {
        isExtracting.value = false;
      }
    };

    img.onerror = () => {
      alert($t('themeSelector.imageLoadFailed'));
      isExtracting.value = false;
    };

    img.src = imageSrc;
  } catch (error) {
    console.error('图片处理失败:', error);
    alert($t('themeSelector.imageProcessFailed'));
    isExtracting.value = false;
  }
}
</script>

<template>
  <!-- 自定义主题对话框 -->
  <Dialog v-model:open="showDialog">
    <DialogContent
      class="max-w-[720px] max-h-[85vh] overflow-hidden p-0 gap-0 rounded-2xl border-0 shadow-2xl"
    >
      <!-- 顶部标题栏 -->
      <div class="px-6 py-5 border-b border-black/5 bg-gradient-to-r from-black/5 to-white">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="p-2 bg-blue-100 rounded-xl">
              <Palette class="size-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle class="text-lg font-semibold text-black/90 mb-0">
                {{ $t('themeSelector.createCustomTheme') }}
              </DialogTitle>
              <p class="text-sm text-black/50 mt-0.5">
                {{ $t('themeSelector.createThemeDesc') }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <!-- 内容区域 -->
      <div class="px-4 py-4 space-y-4 overflow-y-auto max-h-[calc(85vh-200px)]">
        <!-- 基本信息 -->
        <div class="space-y-4">
          <div class="space-y-2">
            <Label for="theme-name" class="text-sm font-medium text-black/70">
              {{ $t('themeSelector.themeName') }}
            </Label>
            <Input
              id="theme-name"
              v-model="customThemeForm.label"
              :placeholder="$t('themeSelector.themeNamePlaceholder')"
              class="h-11 rounded-xl border-black/10 focus:border-blue-400 focus:ring-blue-100"
            />
          </div>
        </div>

        <!-- 智能配色提取 -->
        <div class="space-y-4">
          <Label class="text-sm font-medium text-black/70">
            {{ $t('themeSelector.smartColorExtraction') }}
          </Label>

          <div class="space-y-3">
            <!-- 图片上传区域 -->
            <div
              v-if="!imagePreview"
              class="relative border-2 border-dashed border-black/20 rounded-xl p-6 hover:border-blue-400 hover:bg-blue-50/30 transition-all duration-200 cursor-pointer group"
              @click="triggerImageUpload"
            >
              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleImageUpload"
              />

              <div class="text-center">
                <div
                  class="mx-auto w-12 h-12 bg-black/5 rounded-xl flex items-center justify-center mb-3 group-hover:bg-blue-100 transition-colors duration-200"
                >
                  <Upload class="size-6 text-black/40 group-hover:text-blue-500" />
                </div>
                <p class="text-sm font-medium text-black/70 mb-1">
                  {{ $t('themeSelector.uploadImage') }}
                </p>
                <p class="text-xs text-black/50">
                  {{ $t('themeSelector.supportedFormats') }}
                </p>
              </div>
            </div>

            <!-- 图片预览和操作 -->
            <div v-else class="space-y-3">
              <div class="relative rounded-xl overflow-hidden border border-black/10">
                <img
                  :src="imagePreview"
                  :alt="$t('themeSelector.imagePreview', '图片预览')"
                  class="w-full h-32 object-cover"
                />
                <div class="absolute top-2 right-2 flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    @click="triggerImageUpload"
                    class="h-8 px-3 bg-white/90 backdrop-blur-sm border-white/50 hover:bg-white text-xs"
                  >
                    <Upload class="size-3 mr-1" />
                    {{ $t('themeSelector.replaceImage') }}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    @click="removeImage"
                    class="h-8 px-3 bg-white/90 backdrop-blur-sm border-white/50 hover:bg-white text-xs"
                  >
                    <X class="size-3" />
                  </Button>
                </div>

                <!-- 提取进度指示 -->
                <div
                  v-if="isExtracting"
                  class="absolute inset-0 bg-black/50 flex items-center justify-center"
                >
                  <div class="bg-white rounded-lg px-4 py-2 flex items-center gap-2">
                    <div
                      class="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"
                    ></div>
                    <span class="text-sm font-medium text-black/70">
                      {{ $t('themeSelector.extractingColors') }}
                    </span>
                  </div>
                </div>
              </div>

              <input
                ref="fileInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="handleImageUpload"
              />
            </div>
          </div>
        </div>

        <!-- 自定义配色 -->
        <div class="space-y-4">
          <Label class="text-sm font-medium text-black/70">
            {{ $t('themeSelector.customColors') }}
          </Label>

          <div class="grid grid-cols-2 gap-4">
            <div v-for="(color, index) in customThemeForm.colors" :key="index" class="space-y-2">
              <Label class="text-xs font-medium text-black/60">
                {{
                  index === 0
                    ? $t('themeSelector.primaryColorLabel')
                    : index === 1
                      ? $t('themeSelector.secondaryColorLabel')
                      : index === 2
                        ? $t('themeSelector.color3Label')
                        : $t('themeSelector.color4Label')
                }}
              </Label>

              <div class="space-y-2">
                <!-- 颜色选择器 -->
                <div class="relative group">
                  <div
                    class="w-full h-12 rounded-xl border-2 border-black/10 cursor-pointer overflow-hidden relative group-hover:border-black/20 transition-colors duration-200"
                    :style="{ backgroundColor: color }"
                  >
                    <input
                      :value="color"
                      type="color"
                      class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                      @input="updateCustomColor(index, $event.target.value)"
                    />
                    <div class="absolute inset-0 flex items-center justify-center">
                      <span
                        class="text-xs font-mono bg-black/70 text-white px-2 py-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                      >
                        {{ color }}
                      </span>
                    </div>
                  </div>
                </div>

                <!-- 文本输入框 -->
                <Input
                  :value="color"
                  type="text"
                  placeholder="#000000"
                  class="h-9 text-xs font-mono rounded-lg border-black/10 focus:border-blue-400 focus:ring-blue-100"
                  @input="updateCustomColor(index, $event.target.value)"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- 实时预览 -->
        <div class="space-y-3">
          <div class="flex items-center gap-2">
            <Eye class="size-4 text-black/50" />
            <Label class="text-sm font-medium text-black/70">
              {{ $t('themeSelector.preview') }}
            </Label>
          </div>

          <div class="p-4 bg-black/5 rounded-xl border border-black/10">
            <!-- 配色条 -->
            <div class="flex gap-1 mb-4">
              <div
                v-for="color in customThemeForm.colors"
                :key="color"
                class="flex-1 h-8 rounded-lg border border-black/10"
                :style="{ backgroundColor: color }"
              ></div>
            </div>

            <!-- 应用示例 -->
            <div class="space-y-3">
              <div
                class="h-10 rounded-lg flex items-center px-4 text-sm font-medium shadow-sm"
                :style="{
                  backgroundColor: customThemeForm.primaryColor,
                  color: customThemeForm.secondaryColor,
                }"
              >
                {{ customThemeForm.label || $t('themeSelector.themePreview') }}
              </div>

              <div
                class="p-3 rounded-lg border"
                :style="{
                  backgroundColor: customThemeForm.colors[2],
                  borderColor: customThemeForm.colors[3],
                  color: customThemeForm.primaryColor,
                }"
              >
                <div class="text-xs font-medium mb-1">
                  {{ $t('themeSelector.cardExample') }}
                </div>
                <div class="text-xs text-black/60">
                  {{ $t('themeSelector.cardExampleDesc') }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 底部操作栏 -->
      <div class="px-6 py-4 border-t border-black/5 bg-black/5/50 flex justify-end gap-3">
        <Button
          variant="outline"
          @click="onDialogClose"
          class="h-10 px-6 rounded-xl border-black/10 hover:bg-white"
        >
          {{ $t('themeSelector.cancel') }}
        </Button>
        <Button
          :disabled="!customThemeForm.label.trim()"
          @click="saveCustomTheme"
          class="h-10 px-6 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
        >
          {{ $t('themeSelector.saveTheme') }}
        </Button>
      </div>
    </DialogContent>
  </Dialog>
</template>
